
import Vue from "vue";
import Highcharts from "highcharts";
import heatmapInit from "highcharts/modules/heatmap";
import { required } from "vuelidate/lib/validators";

import { Chart } from "highcharts-vue";
import NoDataRVS from "@/components/NoDataRVS.vue";
import LoadingGraph from "@/components/LoadingGraph.vue";
import dayjs from "dayjs";
import DateTimeRange from "@/components/DateTimeRange.vue";

heatmapInit(Highcharts);

export default Vue.extend({
  name: "RvsFailureModeGraph",
  components: {
    highcharts: Chart,
    NoDataRVS,
    LoadingGraph,
    DateTimeRange,
  },
  validations: {
    isSensor: { required },
    validationGroup: ["isSensor"],
  },
  computed: {
    getModelCode() {
      return this.$store.getters.model?.modelCode;
    },
    getModelId() {
      return this.$store.getters.model?.id;
    },
  },
  data() {
    const date = new Date();

    return {
      startDate: dayjs(date).subtract(30, "day").format("YYYY-MM-DDT00:00:00"),
      endDate: dayjs(date).format("YYYY-MM-DDT23:59:59"),
      sensorGroup: [],
      isSensor: null,
      isLoadingSensorGroup: false,
      graphResultData: [],
      graphLoading: false,
      chartOptions: {
        chart: {
          type: "heatmap",
          animation: false,
          height: 300,
          marginTop: 40,
          marginBottom: 180,
          events: {
            render: function () {
              const chart = this as any;
              const scrollingDiv = chart.container.querySelector(".highcharts-scrolling");
              if (scrollingDiv) {
                scrollingDiv.style.marginLeft = chart.plotLeft + "px";
                scrollingDiv.style.width = chart.chartWidth - chart.plotLeft + "px";
              }
            },
          },
        },
        title: { text: "Failure Mode" },
        credits: { enabled: false },
        colorAxis: {
          dataClasses: [
            { from: -0.5, to: 0.5, color: "#e3f1e7", name: "Normal" },
            { from: 0.5, to: 1.5, color: "#ffc12b", name: "Warning" },
            { from: 1.5, to: 2.5, color: "#f44336", name: "Alarm" },
            { from: -1, to: -0.5, color: "#bdbdbd", name: "No data" },
          ],
        },
        xAxis: {
          categories: [],
          labels: {
            rotation: -90,
            align: "right",
            style: { fontSize: "11px" },
            formatter: function () {
              // "2024-04-01 09:45:51" → "01/04/2024 09:45 น."
              const val = String((this as any).value);
              const match = val.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/);
              if (!match) return val;
              return `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]} น.`;
            },
          },
          title: { text: "" },
        },
        yAxis: {
          categories: [],
          title: { text: "" },
          labels: { style: { fontSize: "12px" } },
          reversed: true,
        },
        legend: {
          align: "center",
          layout: "horizontal",
          verticalAlign: "bottom",
        },
        tooltip: {
          useHTML: true,
          outside: true,
          formatter: function () {
            const statusMap: Record<string, string> = { "-1": "No data", "0": "Normal", "1": "Warning", "2": "Alarm" };
            const status = statusMap[String((this as any).point.value)] ?? "-";
            const percent =
              (status === "Warning" || status === "Alarm") && (this as any).point.percent != null
                ? ` (${(this as any).point.percent}%)`
                : "";
            const rawTime = String((this as any).series.xAxis.categories[(this as any).point.x]);
            const tm = rawTime.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/);
            const formattedTime = tm ? `${tm[3]}/${tm[2]}/${tm[1]} ${tm[4]}:${tm[5]} น.` : rawTime;
            return `<div style="padding:6px">
              <b>${(this as any).series.yAxis.categories[(this as any).point.y]}</b><br/>
              Time: ${formattedTime}<br/>
              Status: <b>${status}</b>${percent}
            </div>`;
          },
        },
        series: [
          {
            name: "Failure Mode",
            borderWidth: 2,
            borderColor: "#ffffff",
            borderRadius: 8,
            data: [],
            dataLabels: { enabled: false },
            turboThreshold: 0,
          },
        ],
      },
    };
  },
  async created() {
    await this.fetchSensorGroup();
  },
  methods: {
    async fetchGraphData() {
      try {
        const data = await (this as any).$dep.modelUseCase.getFailureModeRvsGraph(
          this.getModelCode,
          this.isSensor.sensorId,
          this.startDate,
          this.endDate
        );
        const graphData = data;

        this.graphResultData = graphData.series;

        const FAILURE_MODE_ORDER = [
          "Unbalance",
          "Misalignment",
          "Bearing defect",
          "Bearing lack of lubrication",
          "Bearing looseness",
          "Gear wear",
          "Gear eccentric",
          "Gear misalignment",
          "Gear broken tooth",
          "Belt looseness",
          "Belt alignment",
          "Fluid-related — Blade pass",
          "Fluid-related — Cavitation",
          "Fluid-related — Flow pattern",
          "Soft foot",
          "Foundation distorted",
          "Motor eccentric",
          "Motor rotor bar problem",
          "Impact",
          "Transmitted vibration",
        ];

        const sortedData = [...this.graphResultData].sort((a, b) => {
          const idxA = FAILURE_MODE_ORDER.indexOf(a.name);
          const idxB = FAILURE_MODE_ORDER.indexOf(b.name);
          const rankA = idxA === -1 ? FAILURE_MODE_ORDER.length : idxA;
          const rankB = idxB === -1 ? FAILURE_MODE_ORDER.length : idxB;
          if (rankA !== rankB) return rankA - rankB;
          return a.name.localeCompare(b.name);
        });
        const failureModeNames = sortedData.map((s) => s.name);
        const timestamps = graphData.timestamp;
        const heatmapData: any[] = [];

        sortedData.forEach((series, yIdx) => {
          series.data.forEach((point, xIdx) => {
            heatmapData.push({
              x: xIdx,
              y: yIdx,
              value: point.y ?? -1,
              percent: point.percent,
            });
          });
        });

        const CELL_HEIGHT = 30;
        const CELL_WIDTH = 16;
        const MARGIN_TOP = 40;
        const MARGIN_BOTTOM = 150;

        const chartHeight = Math.max(200, failureModeNames.length * CELL_HEIGHT + MARGIN_TOP + MARGIN_BOTTOM);
        const minPlotWidth = Math.max(timestamps.length * CELL_WIDTH, 400);
        const tickInterval = 8;

        setTimeout(() => {
          this.chartOptions.chart = {
            ...this.chartOptions.chart,
            height: chartHeight,
            scrollablePlotArea: {
              minWidth: minPlotWidth,
              scrollPositionX: 0,
            },
          };
          this.chartOptions.xAxis = {
            ...this.chartOptions.xAxis,
            categories: timestamps,
            tickInterval,
          };
          this.chartOptions.yAxis = {
            ...this.chartOptions.yAxis,
            categories: failureModeNames,
          };
          this.chartOptions.series = [
            {
              ...this.chartOptions.series[0],
              data: heatmapData,
            },
          ];
        }, 500);
      } catch (error) {
        this.graphResultData = [];
      }
    },
    async fetchSensorGroup() {
      try {
        this.isLoadingSensorGroup = true;

        const datas = await (this as any).$dep.modelUseCase.getRvsSensorType(this.getModelId);

        this.sensorGroup = datas;
      } catch (error) {
        this.sensorGroup = [];
      } finally {
        this.isLoadingSensorGroup = false;
      }
    },
    async onChangeSensorType(val: any) {
      this.isSensor = {
        id: val.id,
        sensorId: val.sensorId,
        sensorName: val.sensorName,
      };
      this.graphLoading = true;
      await this.fetchGraphData();
      this.graphLoading = false;
    },
    async onChangeDate(ev: any) {
      this.startDate = dayjs(ev.startDate).subtract(7, "hour").format("YYYY-MM-DD HH:mm:ss");
      this.endDate = dayjs(ev.endDate).subtract(7, "hour").format("YYYY-MM-DD HH:mm:ss");
      this.graphLoading = true;
      await this.fetchGraphData();
      this.graphLoading = false;
    },
  },
  watch: {
    async getModelId() {
      this.sensorGroup = [];
      this.isSensor = null;
      this.graphResultData = [];

      await this.fetchSensorGroup();
    },
  },
});
